import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useActiveBanner } from "@/hooks/useActiveBanner";
import { useNextEvent } from "@/hooks/useNextEvent";

const STORAGE_KEY = "deepc_banner_dismissed";

type ResolvedBanner = {
  id: string;
  text: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
};

/** Slim, editorial announcement bar. Sits above Header on the homepage. */
export default function AnnouncementBanner() {
  const { pathname } = useLocation();
  const { data: dbBanner } = useActiveBanner();
  const { data: nextEvent } = useNextEvent({ upcomingOnly: true });
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  useEffect(() => {
    setDismissedId(localStorage.getItem(STORAGE_KEY));
  }, []);

  // Singleton override pattern: hidden → nothing, override → custom row, auto → event banner.
  const banner: ResolvedBanner | null = useMemo(() => {
    if (dbBanner?.mode === "hidden") return null;

    if (dbBanner?.mode === "override") {
      if (!dbBanner.text?.trim()) return null;
      return {
        id: `override:${dbBanner.id}`,
        text: dbBanner.text,
        ctaLabel: dbBanner.cta_label,
        ctaUrl: dbBanner.cta_url,
      };
    }

    // mode === "auto" (default) or no row yet
    if (nextEvent) {
      const locationPart = nextEvent.isVirtual
        ? "Online"
        : nextEvent.location?.trim() || null;
      const tail = [locationPart, nextEvent.dateLabel].filter(Boolean).join(" · ");
      const text = tail
        ? `Meet us at ${nextEvent.title} — ${tail}`
        : `Meet us at ${nextEvent.title}`;
      return {
        id: `event:${nextEvent.href}`,
        text,
        ctaLabel: "View event",
        ctaUrl: nextEvent.href,
      };
    }
    return null;
  }, [nextEvent, dbBanner]);


  useEffect(() => {
    const visible = banner && dismissedId !== banner.id && !pathname.startsWith("/admin");
    document.documentElement.style.setProperty("--banner-h", visible ? "40px" : "0px");
    return () => {
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [banner, dismissedId, pathname]);

  if (!banner || pathname.startsWith("/admin")) return null;
  if (dismissedId === banner.id) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, banner.id);
    setDismissedId(banner.id);
  };

  const ctaUrl = banner.ctaUrl;
  const isExternal = ctaUrl && /^https?:\/\//.test(ctaUrl);
  const hasCta = Boolean(banner.ctaLabel && ctaUrl);

  return (
    <div
      className="relative z-40 text-primary-foreground border-b border-primary/40"
      style={{ background: "linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.85) 100%)" }}
    >
      <div className="container relative flex items-center justify-center gap-4 py-2.5 pr-10 sm:pr-12">
        <div className="flex items-center gap-3 min-w-0 text-[13px]">
          <span className="hidden sm:inline-block size-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
          <span className="truncate text-primary-foreground/90 font-normal">{banner.text}</span>
          {hasCta &&
            (isExternal ? (
              <a
                href={ctaUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-2.5 py-1 text-[12px] font-medium text-white transition-colors"
              >
                {banner.ctaLabel}
                <span aria-hidden>→</span>
              </a>
            ) : (
              <Link
                to={ctaUrl || "/contact"}
                className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-2.5 py-1 text-[12px] font-medium text-white transition-colors"
              >
                {banner.ctaLabel}
                <span aria-hidden>→</span>
              </Link>
            ))}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="absolute right-4 shrink-0 inline-flex items-center justify-center size-7 rounded-md text-seabed-foreground/65 hover:text-seabed-foreground hover:bg-seabed-foreground/10 transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
